import axios from 'axios';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import queryString from 'query-string';
import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import BoxesIllustration from '../../assets/images/Login/Boxes.svg?url';
import customConfirmAlert from '../../components/ConfirmAlert';
import { LoadingPlaceholder } from '../../components/LoadingPlaceholder/index';
import useLoggedUser from '../../hooks/useLoggedUser';
import MixpanelProvider from '../../hooks/useMixpanel/MixpanelProvider';
import { useSignUpService } from '../../services/signUpService';
import type { IInviteLink } from '../../services/signUpService/types';
import Challenges from '../Challenges';
import loginStyles from '../Login/index.module.scss';
import { LoginHeader } from '../Login/LoginHeader';
import styles from './index.module.scss';
import SelectSignUpMethod from './SelectSignUpMethod';
import SetUpAccountForm from './SetUpAccountForm';
import SignUpContent from './SignUpContent';
import WelcomeScreen from './WelcomeScreen';

function SignUp() {
  const { hash } = useLocation();
  const { inviteKey } = useParams();
  const navigate = useNavigate();

  const {
    state,
    state: { isWelcomeState },
    signUpWithToken,
  } = useSignUpService();
  const { cleanAppCookie } = useLoggedUser();

  const urlParams = queryString.parse(hash);

  const idToken = urlParams?.id_token as string;
  const inviteKeyState = urlParams?.state as string;

  useEffect(() => {
    const checkInvite = async () => {
      try {
        const invite = await axios.get<IInviteLink>(
          `/api/camel/auth/check_invite/${inviteKey || inviteKeyState}`,
        );

        state.setInvite(invite.data);
      } catch {
        customConfirmAlert({
          title: 'This invitation link has expired.',
          body: 'Please reach out to your clinic admin to receive a new sign up link.',
          label: ' Continue to login',
          onClick: () => navigate('/login'),
          classes: {
            overlay: styles.signUpExpireOverlay,
            primary: styles.loginBtn,
            secondary: styles.hidden,
            closeBtn: styles.hidden,
            body: styles.body,
          },
          onClickOutside: () => navigate('/login'),
        });
      }
    };

    checkInvite();
    cleanAppCookie();
  }, []);

  useEffect(() => {
    if (inviteKeyState && idToken) {
      signUpWithToken({ token: idToken, invite: inviteKeyState });
    }
  }, [idToken, inviteKeyState]);

  return (
    <MixpanelProvider mixpanel={{ screen: 'signup' }}>
      <div className={loginStyles.root}>
        <div className={classNames('flex', loginStyles.content)}>
          <div
            className={classNames(
              'flex items-center justify-center w-1/2 bg-primary-m3 p-4',
              loginStyles.illustration,
            )}
          >
            <img src={BoxesIllustration} alt="Login illustration" role="img" />
          </div>
          <div
            className={classNames(
              'flex items-center justify-center w-1/2 bg-neutral-m5 p-4',
              loginStyles.formWrapper,
            )}
          >
            <div
              className={classNames(
                loginStyles.formContainer,
                styles.root,
                'rounded-4 w-[33rem]',
                {
                  [loginStyles.welcomeContainer]: isWelcomeState,
                },
              )}
            >
              <LoadingPlaceholder isLoading={!state.invite}>
                <div className={classNames(loginStyles.padding, 'p-16')}>
                  <div className="flex flex-col gap-6 items-center relative">
                    <LoginHeader />
                    <SignUpContent />
                    <SelectSignUpMethod />
                    <Challenges />
                    <WelcomeScreen />
                    <SetUpAccountForm />
                  </div>
                </div>
              </LoadingPlaceholder>
            </div>
          </div>
        </div>
      </div>
    </MixpanelProvider>
  );
}

export default observer(SignUp);
